/**
 * Page Designer bylPlayer component
 * BYL Custom player for video and audio
 */

const BYL_PLAYER_SELECTOR = '.byl-player';
const PLAYER_SELECTOR = `${BYL_PLAYER_SELECTOR}__player`;
const PLAYER_RANGE_SELECTOR = `${BYL_PLAYER_SELECTOR}__range`;
const PLAY_PAUSE_BUTTON_SELECTOR = `${BYL_PLAYER_SELECTOR}__btn-play-pause`;
const PLAY_PAUSE_BUTTON_SMALL_SELECTOR = `${BYL_PLAYER_SELECTOR}__btn-play-pause--small`;
const SEEK_FORWARD_SELECTOR = `${BYL_PLAYER_SELECTOR}__seek-fwd`;
const SEEK_BACKWARD_SELECTOR = `${BYL_PLAYER_SELECTOR}__seek-bwd`;
const CURRENT_TIME_DISPLAY = `${BYL_PLAYER_SELECTOR}__time-display--current`;
const TIME_LEFT_DISPLAY = `${BYL_PLAYER_SELECTOR}__time-display--left`;
const MODAL_BUTTON = `${BYL_PLAYER_SELECTOR}__btn-close`;
const TRACK_BUTTONS_SELECTOR = `${BYL_PLAYER_SELECTOR}__track-btn`;
const SEEK_DURATION = 10;

const { default: getFrontConfig } = require('../common/getFrontConfig');

const FRONT_CONFIG = getFrontConfig();

/**
 * Toggles between playing and pausing the media player.
 * Changes de data atribute of the button so the icon changes.
 * @param {Event} event - The event object
 */
function togglePlaying(event) {
  const bylPlayer = event.target.closest(BYL_PLAYER_SELECTOR);
  const playOrPauseBtn = bylPlayer.querySelector(PLAY_PAUSE_BUTTON_SELECTOR);
  const playOrPauseBtnSmall = bylPlayer.querySelector(
    PLAY_PAUSE_BUTTON_SMALL_SELECTOR
  );
  const player = bylPlayer.querySelector(PLAYER_SELECTOR);
  if (player && player.paused) {
    player.play();
    playOrPauseBtn.dataset.state = 'Play';
    playOrPauseBtnSmall.dataset.state = 'Play';
  } else {
    player.pause();
    playOrPauseBtn.dataset.state = 'Pause';
    playOrPauseBtnSmall.dataset.state = 'Pause';
  }
}

/**
 * Plays given player and updates button states
 * @param {HTMLElement} player Audio or video player
 */
function play(player) {
  const bylPlayer = player.closest(BYL_PLAYER_SELECTOR);
  const playOrPauseBtn = bylPlayer.querySelector(PLAY_PAUSE_BUTTON_SELECTOR);
  const playOrPauseBtnSmall = bylPlayer.querySelector(
    PLAY_PAUSE_BUTTON_SMALL_SELECTOR
  );

  if (player && player.paused) {
    player.play();
    playOrPauseBtn.dataset.state = 'Play';
    playOrPauseBtnSmall.dataset.state = 'Play';
  }
}

/**
 * Seek the current media playback forwards or backwards by a specified amount.
 * @param {Event} event - The event object
 * @param {number} amount - the amount of time to move media progress
 */
function seekToInit(player) {
  // eslint-disable-next-line no-param-reassign
  player.currentTime = 0;
}

/**
 * Seek the current media playback forwards or backwards by a specified amount.
 * @param {Event} event - The event object
 * @param {number} amount - the amount of time to move media progress
 */
function seekPosition(event, amount) {
  const bylPlayer = event.currentTarget.closest(BYL_PLAYER_SELECTOR);
  const player = bylPlayer.querySelector(PLAYER_SELECTOR);
  player.currentTime += amount;
}

/**
 * Seek forward
 * @param {Event} event - The event object
 */
function seekForward(event) {
  seekPosition(event, SEEK_DURATION);
}

/**
 * Seek backwards
 * @param {Event} event - The event object
 */
function seekBackwards(event) {
  seekPosition(event, -SEEK_DURATION);
}

/**
 * Format second to player time format
 * @param {number} secs
 * @returns {string} formatted time
 */
function formatSecondsToTime(secs) {
  let seconds = Math.round(secs);
  const hours = Math.floor(seconds / (60 * 60)).toString();

  const divisorForMinutes = seconds % (60 * 60);
  const minutes = Math.floor(divisorForMinutes / 60).toString();

  const divisorForSeconds = divisorForMinutes % 60;
  seconds = Math.ceil(divisorForSeconds).toString();

  return `${hours.padStart(2, '0')}:${minutes.padStart(
    2,
    '0'
  )}:${seconds.padStart(2, '0')}`;
}

/**
 * Update time displays, both current and time left
 * @param {Event} event - The event object
 */
function updateTimeDisplay(event) {
  const bylPlayer = event.currentTarget.closest(BYL_PLAYER_SELECTOR);
  const player = bylPlayer.querySelector(PLAYER_SELECTOR);

  const progressBar = bylPlayer.querySelector(PLAYER_RANGE_SELECTOR);
  const currentTime = bylPlayer.querySelector(CURRENT_TIME_DISPLAY);
  const timeLeft = bylPlayer.querySelector(TIME_LEFT_DISPLAY);

  if (Number.isNaN(player.duration)) {
    return;
  }
  progressBar.value = (player.currentTime / player.duration) * 100;
  currentTime.innerText = formatSecondsToTime(player.currentTime);
  timeLeft.innerText = `-${formatSecondsToTime(
    player.duration - player.currentTime
  )}`;
}

/**
 * Show/hide custom controls
 * @param {Event} event - The event object
 */
function activateControls(event) {
  const bylPlayer = event.currentTarget.closest(BYL_PLAYER_SELECTOR);
  const controlsHeader = bylPlayer.querySelector(
    '.byl-player__controls-header'
  );
  const controls = bylPlayer.querySelector('.byl-player__controls');
  const progress = bylPlayer.querySelector('.byl-player__progress');

  controlsHeader?.classList.toggle('active');
  controls?.classList.toggle('active');
  progress?.classList.toggle('active');
}

/**
 * Toggles audio visibility mode, normal (small) or open
 * @param {Event} event - The event object
 */
function handleAudioVisibility(event) {
  const bylPlayer = event.currentTarget.closest(BYL_PLAYER_SELECTOR);
  bylPlayer.classList.toggle('byl-player--audio--open');

  const modalBackground = document.querySelector('.byl-player__backdrop');
  if (modalBackground && modalBackground.style.display === 'block') {
    modalBackground.style.display = 'none';
    bylPlayer.style.display = 'none';
  } else {
    modalBackground.style.display = 'block';
  }
}

/**
 * Marks clicked button as selected
 * @param {HTMLElement} button track button
 */
function markTrackButtonSelected(button) {
  const allTrackButtons = button
    .closest(BYL_PLAYER_SELECTOR)
    .querySelectorAll(TRACK_BUTTONS_SELECTOR);
  allTrackButtons.forEach((btn) => {
    btn.classList.remove('selected');
  });

  button.classList.add('selected');
}

/**
 * Selects clicked audio track, and plays from init
 * @param {Event} event - The event object
 */
function selectAudioTrack(event) {
  const mediaTrack = event.currentTarget.closest('.media-track');
  const bylPlayer = event.currentTarget.closest(BYL_PLAYER_SELECTOR);
  const player = bylPlayer.querySelector(PLAYER_SELECTOR);
  player.src = mediaTrack.querySelector('source').src;
  seekToInit(player);
  play(player);
  markTrackButtonSelected(event.currentTarget);
}

/**
 * Initializes audio player with the track
 *  corresponding to the Site language
 * @param {*} player
 */
function initAudioMainTrack(player) {
  const bylPlayer = player.closest(BYL_PLAYER_SELECTOR);

  const siteLanguage = FRONT_CONFIG.languageCode;
  const siteSource = bylPlayer.querySelector(`.track__${siteLanguage}`);
  // eslint-disable-next-line no-param-reassign
  player.src = siteSource.src;

  markTrackButtonSelected(
    bylPlayer.querySelector(
      `${TRACK_BUTTONS_SELECTOR}[data-lang="${siteLanguage}"]`
    )
  );
}

/**
 * Set up media player
 * @param {HTMLElement} media media element (audio or video)
 * @param {HTMLElement} playPauseBtn playpause button
 * @param {HTMLElement} progressBar progress bar (renge input)
 * @param {HTMLElement} seekForwardBtn seek forward btn
 * @param {HTMLElement} seekBackwardBtn seek backward btn
 * @param {HTMLElement} currentTime current time element
 * @param {HTMLElement} timeLeftDisplay time left element
 */
function setupPlayer(
  media,
  playPauseBtn,
  playPauseSmallBtn,
  progressBar,
  seekForwardBtn,
  seekBackwardBtn,
  currentTime,
  modalButton,
  trackButtons
) {
  if (!media) {
    return;
  }

  /* eslint-disable listeners/no-missing-remove-event-listener */
  if (media.classList.contains('byl-player__video')) {
    media.addEventListener('click', activateControls);
  } else if (media.classList.contains('byl-player__audio')) {
    initAudioMainTrack(media);
    modalButton?.addEventListener('click', handleAudioVisibility);
    trackButtons.forEach((trackButton) => {
      trackButton.addEventListener('click', selectAudioTrack);
    });
  }

  if (playPauseBtn) {
    playPauseBtn.addEventListener('click', togglePlaying);
  }

  if (playPauseSmallBtn) {
    playPauseSmallBtn.addEventListener('click', togglePlaying);
  }

  if (progressBar && currentTime) {
    media.addEventListener('timeupdate', updateTimeDisplay);
  }

  if (seekForwardBtn && seekBackwardBtn) {
    seekForwardBtn.addEventListener('click', seekForward);
    seekBackwardBtn.addEventListener('click', seekBackwards);
  }
  /* eslint-enable listeners/no-missing-remove-event-listener */
}

/**
 * Set up Media Player
 */
function setupMediaPlayer() {
  const players = document.querySelectorAll('.byl-player__player');

  players.forEach((player) => {
    const bylPlayer = player.closest(BYL_PLAYER_SELECTOR);

    setupPlayer(
      player,
      bylPlayer.querySelector(PLAY_PAUSE_BUTTON_SELECTOR),
      bylPlayer.querySelector(PLAY_PAUSE_BUTTON_SMALL_SELECTOR),
      bylPlayer.querySelector(PLAYER_RANGE_SELECTOR),
      bylPlayer.querySelector(SEEK_FORWARD_SELECTOR),
      bylPlayer.querySelector(SEEK_BACKWARD_SELECTOR),
      bylPlayer.querySelector(CURRENT_TIME_DISPLAY),
      bylPlayer.querySelector(MODAL_BUTTON),
      bylPlayer.querySelectorAll(TRACK_BUTTONS_SELECTOR)
    );
  });

  document.removeEventListener('DOMContentLoaded', setupMediaPlayer);
}

const { onDOMContentLoaded } = require('../common/utils/domEvents');

onDOMContentLoaded(() => {
  setupMediaPlayer();
});
